import { CurrentState } from "../../state/currentState";
import { PastProfile } from "./pastProfileTypes";

export interface SimilarityFeatureWeights {
  devProgress: number;
  deltaBT: number;
  deltaROR: number;
}
export const DEFAULT_SIMILARITY_FEATURE_WEIGHTS: SimilarityFeatureWeights = {
  devProgress: 0.4,
  deltaBT: 0.35,
  deltaROR: 0.25,
};
export interface RankedProfile {
  id: string;
  profile: PastProfile;
  distance: number;
  reasons: string[];
}

export interface SimilarityVector {
  devProgress: number;
  deltaBT: number;
  deltaROR: number; // scalar or vector distance
}

export type SimilarityMode = "simple" | "ror_segment_3" | "ror_segment_5";

//pastProfiles 개수 → mode 선택
export function chooseSimilarityMode(pastCount: number): SimilarityMode {
  if (pastCount < 30) return "simple";
  if (pastCount < 100) return "ror_segment_3";
  return "ror_segment_5";
}

//devProgress (dryEnd 기준 보정)
function computeDevProgress(curr: CurrentState, past: PastProfile): number {
  return curr.tDev / past.devTime;
}

function computeRORDistance(currRORVec: number[], pastRORVec: number[]): number {
  let sum = 0;
  for (let i = 0; i < currRORVec.length; i++) {
    const d = currRORVec[i] - pastRORVec[i];
    sum += d * d;
  }
  return Math.sqrt(sum);
}

/* ============================
 * Feature Vector
 * ============================ */

/**
 * CurrentState ↔ PastProfile을 비교 가능한 feature 공간으로 변환
 */

function buildRORSegments(rorSeries: number[], segmentCount: number): number[] {
  const segSize = Math.floor(rorSeries.length / segmentCount);
  const segments: number[] = [];

  for (let i = 0; i < segmentCount; i++) {
    const start = i * segSize;
    const end = i === segmentCount - 1 ? rorSeries.length : start + segSize;

    let sum = 0;
    let count = 0;

    for (let j = start; j < end; j++) {
      sum += rorSeries[j];
      count++;
    }

    segments.push(count ? sum / count : 0);
  }

  return segments;
}
function getPastRORSegments(past: PastProfile, segmentCount: number): number[] {
  return Array(segmentCount).fill(past.meanROR);
}
function getCurrentRORSegments(state: CurrentState, recentRORSeries: number[], segmentCount: number): number[] {
  return buildRORSegments(recentRORSeries, segmentCount);
}

export function buildSimilarityVector(
  curr: CurrentState,
  past: PastProfile,
  mode: SimilarityMode,
  recentRORSeries: number[]
): SimilarityVector {
  const devProgress = computeDevProgress(curr, past);
  const deltaBT = curr.BT - past.dropBT;

  let deltaROR: number;

  if (mode === "simple") {
    deltaROR = curr.ROR - past.meanROR;
  } else {
    const segCount = mode === "ror_segment_3" ? 3 : 5;

    const currSeg = getCurrentRORSegments(curr, recentRORSeries, segCount);
    const pastSeg = getPastRORSegments(past, segCount);

    deltaROR = computeRORDistance(currSeg, pastSeg);
  }

  return { devProgress, deltaBT, deltaROR };
}

// function buildSimilarityVector(curr: CurrentState, past: PastProfile) {
//   return {
//     devProgress: curr.tDev / past.devTime - 0.5,
//     deltaBT: curr.BT - past.dropBT,
//     deltaROR: curr.ROR - past.meanROR,
//   };
// }

/* ============================
 * Distance
 * ============================ */

/**
 * 가중 feature 거리 계산
 */
// function computeSimilarityDistance(curr: CurrentState, past: PastProfile, w: SimilarityFeatureWeights): number {
//   const devProgressCurr = curr.tDev / past.devTime;
//   const devProgressPast = 1; // 기준값

//   const devProgressDiff = devProgressCurr - devProgressPast;
//   const deltaBTDiff = curr.BT - past.dropBT;
//   const deltaRORDiff = curr.ROR - past.meanROR;

//   return Math.sqrt(
//     w.devProgress * devProgressDiff ** 2 + w.deltaBT * deltaBTDiff ** 2 + w.deltaROR * deltaRORDiff ** 2
//   );
// }

export function computeSimilarityDistance(v: SimilarityVector, w: SimilarityFeatureWeights): number {
  return w.devProgress * (v.devProgress - 1) ** 2 + w.deltaBT * (v.deltaBT / 30) ** 2 + w.deltaROR * v.deltaROR ** 2;
}

/* ============================
 * Explanation
 * ============================ */

/**
 * 프로파일 선택 사유 생성 (설명용)
 */
function buildSelectionReasons(curr: CurrentState, past: PastProfile): string[] {
  const reasons: string[] = [];

  if (Math.abs(curr.tDev / past.devTime - 1) < 0.1) {
    reasons.push("dev_progress");
  }

  if (Math.abs(curr.BT - past.dropBT) < 3) {
    reasons.push("bt_position");
  }

  if (Math.abs(curr.ROR - past.meanROR) < 0.3) {
    reasons.push("ror_pattern");
  }

  return reasons;
}

/* ============================
 * Ranking
 * ============================ */

/**
 * CurrentState 기준 PastProfile 정렬
 */
export function rankPastProfiles(
  state: CurrentState,
  pastProfiles: PastProfile[],
  k: number,
  weights: SimilarityFeatureWeights,
  recentRORSeries: number[]
): RankedProfile[] {
  const mode = chooseSimilarityMode(pastProfiles.length);

  return pastProfiles
    .map((past) => {
      const vec = buildSimilarityVector(state, past, mode, recentRORSeries);
      return {
        id: past.id,
        profile: past,
        distance: computeSimilarityDistance(vec, weights),
        reasons: buildSelectionReasons(state, past),
      };
    })
    .sort((a, b) => a.distance - b.distance)
    .slice(0, k);
}


/* ============================
 * Clustering
 * ============================ */

/**
 * 거리 기준 간단 군집화
 */
export function clusterRankedProfiles(profiles: RankedProfile[]) {
  return {
    veryClose: profiles.filter((p) => p.distance < 0.4),
    close: profiles.filter((p) => p.distance >= 0.4 && p.distance < 1.0),
    far: profiles.filter((p) => p.distance >= 1.0),
  };
}
